
import { useEffect, useState } from "react";
import { getMyAttendanceApi } from "../../app/api";
import { createPortal } from "react-dom";


export default function MyAttendance() {
  const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);

  // ✅ Pagination
  const [page, setPage] = useState(1);
  const recordsPerPage = 5;

  // ✅ Monthly Filter
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  // ✅ FETCH FUNCTION (define BEFORE useEffect)
  const loadAttendance = async () => {
    setLoading(true);

    const [year, month] = selectedMonth.split("-");

    const res = await getMyAttendanceApi(month, year);

    if (res?.success && Array.isArray(res.data)) {
      setRecords(res.data);
    } else {
      setRecords([]);
    }

    setPage(1);
    setLoading(false);
  };

  // ✅ CALL API WHEN MONTH CHANGES
  useEffect(() => {
    loadAttendance();
  }, [selectedMonth]);

  // ✅ Helpers
  const formatMinutes = (min) => {
    if (!min) return "0m";
    return `${min}m`;
  };

  const formatDate = (date) =>
    typeof date === "string" ? date.split("T")[0] : "--";

  // ✅ FILTER BY MONTH (optional if API already filtered)
  const filteredRecords = records.filter((row) =>
    row.attendance_date?.startsWith(selectedMonth)
  );

  // ✅ PAGINATION (use filteredRecords, NOT records)
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  const startIndex = (page - 1) * recordsPerPage;

  const paginatedRecords = filteredRecords.slice(
    startIndex,
    startIndex + recordsPerPage
  );

  // ✅ RESET PAGE IF DATA CHANGES
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [filteredRecords]);

  if (loading) {
    return <div className="p-6 text-center text-gray-600">Loading attendance...</div>;
  }
  return (
    <>
      <div className="rounded-2xl p-3 md:p-5 bg-[#12082F] text-white shadow-2xl md:mt-20">

        {/* HEADER */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
          <h2 className="font-semibold text-base md:text-xl">
            My Attendance
          </h2>

          {/* ✅ MONTH FILTER */}
          <div className="flex gap-2 items-center">
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1 rounded-lg bg-white/100 text-black border border-white/20 cursor-pointer"
            />

            <button
              onClick={() => {
                setSelectedMonth(new Date().toISOString().slice(0, 7));
                setPage(1);
              }}
              className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20"
            >
              Reset
            </button>
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto rounded-2xl bg-white/5 backdrop-blur">
          <table className="min-w-[900px] w-full text-sm text-white">
            <thead>
              <tr className="text-left text-gray-300 border-b border-white/10">
                <th className="p-3">Date</th>
                <th className="p-3">Punch In</th>
                <th className="p-3">Img In</th>
                <th className="p-3">Img Out</th>
                <th className="p-3">Punch Out</th>
                <th className="p-3">Total Hours</th>
                <th className="p-3">Break</th>
                <th className="p-3">Status</th>
                <th className="p-3">Early / Extra</th>
              </tr>
            </thead>

            <tbody>
              {paginatedRecords.length > 0 ? (
                paginatedRecords.map((row, i) => (
                  <tr
                    key={i}
                    className="border-b border-white/5 hover:bg-white/10 transition"
                  >
                    <td className="p-3">
                      {formatDate(row.attendance_date)}
                    </td>

                    <td className="p-3">{row.punch_in || "—"}</td>

                    <td className="p-3 text-center">
                      {row.punch_in_img ? (
                        <img
                          src={`${IMAGE_BASE_URL}/${row.punch_in_img}`}
                          className="w-10 h-10 object-cover rounded cursor-pointer border"
                          onClick={() =>
                            setImagePreview(
                              `${IMAGE_BASE_URL}/${row.punch_in_img}`
                            )
                          }
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="p-3 text-center">
                      {row.punch_out_img ? (
                        <img
                          src={`${IMAGE_BASE_URL}/${row.punch_out_img}`}
                          className="w-10 h-10 object-cover rounded cursor-pointer border"
                          onClick={() =>
                            setImagePreview(
                              `${IMAGE_BASE_URL}/${row.punch_out_img}`
                            )
                          }
                        />
                      ) : (
                        "—"
                      )}
                    </td>

                    <td className="p-3">{row.punch_out || "—"}</td>

                    <td className="p-3">
                      {row.total_minutes
                        ? `${Math.floor(row.total_minutes / 60)}h ${row.total_minutes % 60
                        }m`
                        : "—"}
                    </td>

                    <td className="p-3">
                      {formatMinutes(row.break_minutes)}
                    </td>

                    <td className="p-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${row.status === 1
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                          }`}
                      >
                        {row.status === 1 ? "Present" : "Absent"}
                      </span>
                    </td>

                    <td className="p-3 text-xs text-gray-200">
                      Early: {row.early_time || "0m"} <br />
                      Extra: {row.extra_time || "0m"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="p-4 text-center text-gray-400">
                    No Attendance Records Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex justify-between mt-4 text-sm">
          <div className="opacity-70">
            Page {page} of {totalPages || 1}
          </div>

          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1 rounded bg-white/10 disabled:opacity-40"
            >
              Prev
            </button>

            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 rounded bg-purple-600 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* IMAGE MODAL */}
      {imagePreview && createPortal(
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center"
          onClick={() => setImagePreview(null)}
        >
          <div
            className="bg-white p-4 rounded-xl max-w-[90%] max-h-[90%]"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imagePreview}
              className="max-w-full max-h-[80vh] rounded"
            />
          </div>
        </div>,
document.body
      )}
    </>
  );
}
// import { useEffect, useState } from "react";
// import { getMyAttendanceApi } from "../../app/api";

// export default function MyAttendance() {
//   const IMAGE_BASE_URL = "https://uptask.visiontrek.io";
//   const [records, setRecords] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [imagePreview, setImagePreview] = useState(null);

//   const formatMinutes = (min) => {
//     if (min === null || min === undefined) return "0m";
//     return `${min}m`;
//   };

//   const formatDate = (date) =>
//     typeof date === "string" ? date.split("T")[0] : "--";

//   useEffect(() => {
//     getMyAttendanceApi().then((res) => {
//       if (res.success && Array.isArray(res.data)) {
//         setRecords(res.data);
//       }
//       setLoading(false);
//     });
//   }, []);

//   if (loading)
//     return (
//       <div className="text-white/70 text-center py-10">
//         Loading attendance...
//       </div>
//     );

//   return (
//     <>
//       <div className="rounded-2xl mt-5 md:mt-20 p-5 bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f] text-white border border-white/10 shadow-2xl relative">
//         <h3 className="font-bold text-2xl mb-4">
//           My Attendance
//         </h3>

//         {/* ================= DESKTOP TABLE ================= */}
//         <div className="hidden md:block max-h-[420px] overflow-y-auto">
//           <table className="w-full text-sm border-separate border-spacing-x-3">
//             <thead className="text-white/70">
//               <tr className="border-b border-white/10">
//                 <th className="px-3">Date</th>
//                 <th className="px-3">In</th>
//                 <th className="px-3">Img In</th>
//                 <th className="px-3">Img Out</th>
//                 <th className="px-3">Out</th>
//                 <th className="px-3">Total</th>
//                 <th className="px-3">Break</th>
//                 <th className="px-3">Status</th>
//                 <th className="px-3">Early / Extra</th>
//               </tr>
//             </thead>

//             <tbody>
//               {records.map((row, i) => (
//                 <tr key={i} className="border-b border-white/5 hover:bg-white/5">
//                   <td className="px-3 py-2">
//                     {formatDate(row.attendance_date)}
//                   </td>
//                   <td className="px-3">{row.punch_in || "—"}</td>
//                   <td className="px-3">
//                     {row.punch_in_img ? (
//                       <img
//                         src={`${IMAGE_BASE_URL}/${row.punch_in_img}`}
//                         className="w-9 h-9 rounded mx-auto cursor-pointer"
//                         onClick={() =>
//                           setImagePreview(
//                             `${IMAGE_BASE_URL}/${row.punch_in_img}`
//                           )
//                         }
//                       />
//                     ) : "—"}
//                   </td>
//                   <td className="px-3">
//                     {row.punch_out_img ? (
//                       <img
//                         src={`${IMAGE_BASE_URL}/${row.punch_out_img}`}
//                         className="w-9 h-9 rounded mx-auto cursor-pointer"
//                         onClick={() =>
//                           setImagePreview(
//                             `${IMAGE_BASE_URL}/${row.punch_out_img}`
//                           )
//                         }
//                       />
//                     ) : "—"}
//                   </td>
//                   <td className="px-3">{row.punch_out || "—"}</td>
//                   <td className="px-3">
//                     {row.total_minutes
//                       ? `${Math.floor(row.total_minutes / 60)}h ${row.total_minutes % 60}m`
//                       : "—"}
//                   </td>
//                   <td className="px-3">
//                     {formatMinutes(row.break_minutes)}
//                   </td>
//                   <td className="px-3">
//                     <span className="px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-300">
//                       {row.status === 1 ? "Present" : "Absent"}
//                     </span>
//                   </td>
//                   <td className="px-3 text-xs">
//                     E: {row.early_time || "0m"} <br />
//                     X: {row.extra_time || "0m"}
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>

//         {/* ================= MOBILE CARDS ================= */}
//         <div className="md:hidden space-y-3">
//           {records.map((row, i) => (
//             <div
//               key={i}
//               className="bg-gradient-to-br from-[#2d1c6a] to-[#12082f] border border-white/10 rounded-2xl p-4 shadow-lg"
//             >
//               <div className="flex justify-between mb-2">
//                 <p className="font-semibold text-blue-300">
//                   {formatDate(row.attendance_date)}
//                 </p>
//                 <span className={`px-3 py-1 rounded-full text-xs ${
//                   row.status === 1
//                     ? "bg-green-500/20 text-green-300"
//                     : "bg-red-500/20 text-red-300"
//                 }`}>
//                   {row.status === 1 ? "Present" : "Absent"}
//                 </span>
//               </div>

//               <div className="grid grid-cols-2 gap-3 text-sm">
//                 <div>
//                   <p className="text-white/60 text-xs">In</p>
//                   <p>{row.punch_in || "—"}</p>
//                 </div>
//                 <div>
//                   <p className="text-white/60 text-xs">Out</p>
//                   <p>{row.punch_out || "—"}</p>
//                 </div>
//                 <div>
//                   <p className="text-white/60 text-xs">Total</p>
//                   <p className="text-blue-300 font-semibold">
//                     {row.total_minutes
//                       ? `${Math.floor(row.total_minutes / 60)}h ${row.total_minutes % 60}m`
//                       : "—"}
//                   </p>
//                 </div>
//                 <div>
//                   <p className="text-white/60 text-xs">Break</p>
//                   <p>{formatMinutes(row.break_minutes)}</p>
//                 </div>
//                 <div>
//                   <p className="text-white/60 text-xs">Early</p>
//                   <p>{row.early_time || "0m"}</p>
//                 </div>
//                 <div>
//                   <p className="text-white/60 text-xs">Extra</p>
//                   <p>{row.extra_time || "0m"}</p>
//                 </div>
//               </div>

//               {(row.punch_in_img || row.punch_out_img) && (
//                 <div className="flex gap-3 mt-4">
//                   {row.punch_in_img && (
//                     <img
//                       src={`${IMAGE_BASE_URL}/${row.punch_in_img}`}
//                       className="w-12 h-12 rounded-lg border border-white/20 object-cover cursor-pointer"
//                       onClick={() =>
//                         setImagePreview(
//                           `${IMAGE_BASE_URL}/${row.punch_in_img}`
//                         )
//                       }
//                     />
//                   )}
//                   {row.punch_out_img && (
//                     <img
//                       src={`${IMAGE_BASE_URL}/${row.punch_out_img}`}
//                       className="w-12 h-12 rounded-lg border border-white/20 object-cover cursor-pointer"
//                       onClick={() =>
//                         setImagePreview(
//                           `${IMAGE_BASE_URL}/${row.punch_out_img}`
//                         )
//                       }
//                     />
//                   )}
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>

//       {/* IMAGE MODAL */}
//       {imagePreview && (
//         <div
//           className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center"
//           onClick={() => setImagePreview(null)}
//         >
//           <img
//             src={imagePreview}
//             className="max-h-[80vh] rounded-xl"
//           />
//         </div>
//       )}
//     </>
//   );
// }
